/**
 * Produtos (17 §3.1; 11 §A; D36–D41, D48–D53): cadastro em uma única tela.
 * - Código gerado pelo banco (a partir de 3016); ninguém digita.
 * - Dados, embalagens (em lote), imagem e ficha técnica são gravados juntos pela função salvar_produto.
 * - NCM pela tabela oficial: sugere a marcação de informática (Anexo III do RICMS-SE) e o grupo.
 * - Cotações e sugestões de imagem aparecem na mesma página depois que o produto existe.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import {
  BooleanInput,
  CreateBase,
  Datagrid,
  EditBase,
  Form,
  FunctionField,
  List,
  Loading,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SaveButton,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  Title,
  regex,
  required,
  useEditContext,
  useGetOne,
  useGetList,
  useInput,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextFieldMui from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { gtinValido, formatarNcm } from "@sobral/dominio";
import { AcoesLista, EntradaListaCrescente, useCelular, vazioParaNulo } from "../componentes";
import { formatarNumero, normalizarBusca } from "../lib/formato";
import { supabase } from "../lib/supabase";
import { DialogoCotacao, PainelCotacoes } from "./cotacoes";
import {
  CampoCodigoBarras,
  EntradaFabricante,
  EntradaNcm,
  SeletorUnidade,
  ZonaArquivo,
  useCodigoBarrasExistente,
  useUnidades,
} from "./entradas-produto";

const BUCKET = "produtos";
const urlPublica = (caminho: string | null | undefined) =>
  caminho ? supabase.storage.from(BUCKET).getPublicUrl(caminho).data.publicUrl : null;
const validarUrl = regex(/^https?:\/\/\S+$/, "Informe o endereço completo, começando com https://");

interface Produto {
  id: string;
  codigo: string;
  descricao: string;
  fabricante_id: string | null;
  fabricante: string | null;
  modelo: string | null;
  grupo_id: string | null;
  informatica: boolean;
  material_escolar: boolean;
  ncm: string | null;
  unidade_base: string;
  url: string | null;
  observacao: string | null;
  ativo: boolean;
  imagem_caminho: string | null;
  ficha_tecnica_caminho: string | null;
}

interface EmbalagemGravada {
  id: string;
  unidade: string;
  descricao: string | null;
  quantidade: number;
  contida_id: string | null;
  fator_base: number;
  base: boolean;
  codigo_barras: string | null;
  codigo_legado: string | null;
  ativo: boolean;
}

/** Linha de embalagem no formulário. `chave` identifica a linha (id quando já gravada). */
interface LinhaEmbalagem {
  chave: string;
  id?: string;
  base: boolean;
  unidade: string;
  quantidade: string;
  contida: string; // "" = quantidade em unidades-base; senão, a chave da embalagem contida
  descricao: string;
  codigo_barras: string;
  ativo: boolean;
  codigo_legado?: string | null;
}

interface ValoresProduto {
  id?: string;
  codigo?: string;
  descricao: string;
  fabricante_id: string | null;
  modelo: string | null;
  grupo_id: string | null;
  informatica: boolean;
  material_escolar: boolean;
  ncm: string | null;
  unidade_base: string;
  url: string | null;
  observacao: string | null;
  ativo: boolean;
  imagem_caminho: string | null;
  ficha_tecnica_caminho: string | null;
  embalagens: LinhaEmbalagem[];
  /** Só na tela: descrição confirmada (ao sair do campo) que dispara a sugestão de imagem. Não é gravado. */
  _termo_imagem?: string;
}

const novaChave = () => `nova-${Math.random().toString(36).slice(2, 10)}`;

function Miniatura({ caminho, tamanho = 40 }: { caminho: string | null; tamanho?: number }) {
  const url = urlPublica(caminho);
  return (
    <Avatar variant="rounded" src={url ?? undefined} sx={{ width: tamanho, height: tamanho, bgcolor: "background.default" }}>
      <ImageIcon fontSize="small" color="disabled" />
    </Avatar>
  );
}

/* ------------------------------------ Lista ------------------------------------ */

/** Registra cotação direto da lista, sem abrir o produto. */
function BotaoNovaCotacao({ produtoId, compacto = false }: { produtoId: string; compacto?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const parar = (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
  };
  return (
    <Box component="span" onClick={parar}>
      {compacto ? (
        <Tooltip title="Registrar cotação">
          <IconButton size="small" onClick={() => setAberto(true)} aria-label="Registrar cotação">
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAberto(true)} sx={{ whiteSpace: "nowrap" }}>
          Cotação
        </Button>
      )}
      <DialogoCotacao aberto={aberto} aoFechar={() => setAberto(false)} produtoId={produtoId} />
    </Box>
  );
}

const filtrosProduto = [
  <TextInput
    key="busca"
    source="busca@ilike"
    label="Pesquisar código, descrição ou fabricante"
    parse={normalizarBusca}
    alwaysOn
    resettable
    sx={{ minWidth: 280 }}
  />,
  <ReferenceInput key="grupo" source="grupo_id" reference="grupo_produto" sort={{ field: "nome", order: "ASC" }}>
    <SelectInput optionText="nome" label="Grupo" />
  </ReferenceInput>,
  <SelectInput
    key="informatica"
    source="informatica"
    label="Informática (fiscal)"
    choices={[
      { id: true, name: "Sim" },
      { id: false, name: "Não" },
    ]}
  />,
  <SelectInput
    key="ativo"
    source="ativo"
    label="Situação"
    choices={[
      { id: true, name: "Ativos" },
      { id: false, name: "Inativos" },
    ]}
  />,
];

export function ProdutoLista() {
  const celular = useCelular();
  return (
    <List
      title="Produtos"
      sort={{ field: "codigo", order: "DESC" }}
      filters={filtrosProduto}
      filterDefaultValues={{ ativo: true }}
      actions={<AcoesLista />}
      exporter={false}
      perPage={25}
    >
      {celular ? (
        <SimpleList
          leftAvatar={(r) => <Miniatura caminho={r.imagem_caminho} />}
          primaryText={(r) => r.descricao}
          secondaryText={(r) => [r.codigo, r.fabricante].filter(Boolean).join(" · ")}
          tertiaryText={(r) => <BotaoNovaCotacao produtoId={r.id as string} compacto />}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <FunctionField label="" render={(r) => <Miniatura caminho={r.imagem_caminho} />} />
          <TextField source="codigo" />
          <TextField source="descricao" />
          <TextField source="fabricante" emptyText="—" />
          <ReferenceField source="grupo_id" reference="grupo_produto" link={false} emptyText="Sem grupo">
            <TextField source="nome" />
          </ReferenceField>
          <FunctionField source="ncm" render={(r) => (r.ncm ? formatarNcm(r.ncm) : "—")} />
          <FunctionField
            source="informatica"
            render={(r) => (r.informatica ? <Chip size="small" variant="outlined" label="Informática" /> : "—")}
          />
          <TextField source="unidade_base" />
          <FunctionField label="" render={(r) => <BotaoNovaCotacao produtoId={r.id as string} />} />
        </Datagrid>
      )}
    </List>
  );
}

/* --------------------------- Seção: identificação --------------------------- */

interface ItemAnexo {
  item: string;
  descricao: string;
}

/** Materiais escolares do RICMS-SE, art. 616-C-A, IV, "c": o FECOEP não incide (D107). */
const MATERIAIS_ESCOLARES =
  "agenda escolar, apontador, borracha (inclusive caneta e lápis borracha), caderno, caneta esferográfica, cartolina e papel cartão, " +
  "classificador, cola escolar, corretivo, estojo, lápis, lapiseira, massa de modelar, papel celofane, pincel de escrever e desenhar, régua e tinta guache";

/** Texto de apoio escondido atrás do ícone (i), para não poluir a tela (D114). */
function ComExplicacao({ children, explicacao }: { children: ReactNode; explicacao: ReactNode }) {
  const [aberta, setAberta] = useState(false);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.25}>
        {children}
        <Tooltip title={aberta ? "Esconder explicação" : "Entender"}>
          <IconButton size="small" onClick={() => setAberta((a) => !a)} aria-label="Explicação" sx={{ color: aberta ? "secondary.main" : "text.secondary" }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Collapse in={aberta}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ pb: 0.5 }}>
          {explicacao}
        </Typography>
      </Collapse>
    </Box>
  );
}

function MaterialEscolar() {
  const { field } = useInput({ source: "material_escolar", defaultValue: false });
  return (
    <ComExplicacao explicacao={<>Sem FECOEP só nos itens do RICMS-SE, art. 616-C-A, IV, "c": {MATERIAIS_ESCOLARES}.</>}>
      <FormControlLabel
        control={<Switch checked={Boolean(field.value)} onChange={(e) => field.onChange(e.target.checked)} />}
        label="Material escolar (sem FECOEP)"
        sx={{ mr: 0 }}
      />
    </ComExplicacao>
  );
}

/**
 * Informática fiscal com sugestão automática (D50): liga quando o NCM está no Anexo III ou, sem NCM,
 * quando o grupo é "Informática". Se a pessoa mexer no interruptor, a escolha dela prevalece.
 */
function InformaticaFiscal({ grupoInformaticaId }: { grupoInformaticaId: string | null }) {
  const { setValue } = useFormContext<ValoresProduto>();
  const ncm = useWatch<ValoresProduto, "ncm">({ name: "ncm" });
  const grupoId = useWatch<ValoresProduto, "grupo_id">({ name: "grupo_id" });
  const { field } = useInput({ source: "informatica" });
  const manual = useRef(false);
  const [anexo, setAnexo] = useState<ItemAnexo | null>(null);
  // NCM com que o formulário abriu: ao carregar um produto gravado, a marcação existente é mantida.
  const ncmInicial = useRef(ncm);

  useEffect(() => {
    let ativo = true;
    if (!ncm) {
      setAnexo(null);
      return;
    }
    void supabase.rpc("ncm_anexo_iii_item", { ncm }).then(({ data }) => {
      if (!ativo) return;
      const item = (data as ItemAnexo[] | null)?.[0] ?? null;
      setAnexo(item);
      if (ncm === ncmInicial.current) return;
      if (!manual.current) setValue("informatica", Boolean(item), { shouldDirty: true });
    });
    return () => {
      ativo = false;
    };
  }, [ncm, setValue]);

  useEffect(() => {
    if (ncm || manual.current || !grupoInformaticaId) return;
    if (grupoId === grupoInformaticaId) setValue("informatica", true, { shouldDirty: true });
  }, [grupoId, ncm, grupoInformaticaId, setValue]);

  return (
    <Box>
      <ComExplicacao
        explicacao={
          anexo
            ? `NCM no Anexo III do RICMS-SE, item ${anexo.item}: ${anexo.descricao}. Confirme se o produto corresponde à descrição.`
            : ncm
              ? "NCM fora do Anexo III do RICMS-SE (art. 40, IX)."
              : "Sugerida pelo NCM (Anexo III do RICMS-SE, art. 40, IX). Pode ser alterada manualmente."
        }
      >
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(field.value)}
              onChange={(e) => {
                manual.current = true;
                field.onChange(e.target.checked);
              }}
            />
          }
          label="Informática (benefício fiscal de ICMS)"
          sx={{ mr: 0 }}
        />
      </ComExplicacao>
      {anexo && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
          Anexo III, item {anexo.item}
        </Typography>
      )}
    </Box>
  );
}

function UnidadeBase() {
  const unidades = useUnidades();
  const { field, fieldState } = useInput({ source: "unidade_base", validate: required("Informe a unidade-base.") });
  return (
    <SeletorUnidade
      rotulo="Unidade-base"
      valor={(field.value as string) || null}
      aoMudar={(sigla) => field.onChange(sigla ?? "")}
      unidades={unidades}
      {...(fieldState.error?.message ? { erro: fieldState.error.message } : {})}
    />
  );
}

interface SugestaoNcm {
  codigo: string;
  codigo_formatado: string;
  descricao_completa: string;
  usado_em: number;
}

interface ProdutoParecido {
  id: string;
  codigo: string;
  descricao: string;
}

/**
 * Assistência da descrição (D58): enquanto se digita, sugere NCMs (tabela oficial + produtos já cadastrados)
 * e mostra produtos parecidos já existentes, para evitar duplicidade.
 */
function DescricaoAssistida({ aoEscolherNcm }: { aoEscolherNcm: (ncm: string) => void }) {
  const descricao = useWatch<ValoresProduto, "descricao">({ name: "descricao" }) ?? "";
  const ncm = useWatch<ValoresProduto, "ncm">({ name: "ncm" });
  const produtoId = useWatch<ValoresProduto, "id">({ name: "id" });
  const [sugestoes, setSugestoes] = useState<SugestaoNcm[]>([]);
  const [parecidos, setParecidos] = useState<ProdutoParecido[]>([]);

  useEffect(() => {
    let ativo = true;
    const texto = descricao.trim();
    if (texto.length < 4) {
      setSugestoes([]);
      setParecidos([]);
      return;
    }
    const espera = setTimeout(() => {
      if (!ncm) {
        void supabase.rpc("sugerir_ncm", { descricao: texto }).then(({ data }) => {
          if (ativo) setSugestoes((data as SugestaoNcm[] | null) ?? []);
        });
      }
      const palavras = normalizarBusca(texto)
        .split(/[^a-z0-9]+/)
        .filter((p) => p.length >= 3)
        .slice(0, 4);
      if (palavras.length === 0) return;
      let consulta = supabase.from("produto").select("id, codigo, descricao").eq("ativo", true);
      for (const p of palavras) consulta = consulta.like("busca", `%${p}%`);
      if (produtoId) consulta = consulta.neq("id", produtoId);
      void consulta.limit(5).then(({ data }) => {
        if (ativo) setParecidos((data as ProdutoParecido[] | null) ?? []);
      });
    }, 500);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [descricao, ncm, produtoId]);

  if ((ncm || sugestoes.length === 0) && parecidos.length === 0) return null;
  return (
    <Stack spacing={0.75} sx={{ mt: -0.5, mb: 1 }}>
      {!ncm && sugestoes.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            NCM sugeridos para esta descrição — clique para usar:
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {sugestoes.map((s) => (
              <Tooltip key={s.codigo} title={s.descricao_completa}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={s.usado_em > 0 ? "secondary" : "default"}
                  label={`${s.codigo_formatado} · ${s.descricao_completa.split(" › ").pop() ?? ""}${s.usado_em > 0 ? ` (usado em ${s.usado_em})` : ""}`}
                  onClick={() => aoEscolherNcm(s.codigo)}
                  sx={{ maxWidth: 360 }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Box>
      )}
      {parecidos.length > 0 && (
        <Alert severity="info" sx={{ py: 0 }}>
          Produtos parecidos já cadastrados:{" "}
          {parecidos.map((p, i) => (
            <span key={p.id}>
              {i > 0 && " · "}
              <Link component={RouterLink} to={`/produto/${p.id}`} target="_blank" rel="noopener">
                {p.codigo} — {p.descricao}
              </Link>
            </span>
          ))}
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Marca lida da descrição (D111), no cadastro novo: a marca cadastrada mais longa que aparece como palavra inteira
 * ("Kingston" em "Ssd Kingston 7.68tb"). Se a pessoa escolher outra marca, a escolha dela prevalece.
 */
function MarcaPelaDescricao() {
  const { setValue } = useFormContext<ValoresProduto>();
  const descricao = useWatch<ValoresProduto, "descricao">({ name: "descricao" }) ?? "";
  const fabricanteId = useWatch<ValoresProduto, "fabricante_id">({ name: "fabricante_id" });
  const automatica = useRef<string | null>(null);
  const { data: fabricantes = [] } = useGetList<{ id: string; nome: string }>("fabricante", {
    filter: { ativo: true },
    pagination: { page: 1, perPage: 2000 },
    sort: { field: "nome", order: "ASC" },
  });
  const escolhidaPelaPessoa = Boolean(fabricanteId) && fabricanteId !== automatica.current;

  useEffect(() => {
    if (escolhidaPelaPessoa || fabricantes.length === 0) return;
    const texto = ` ${normalizarBusca(descricao).replace(/[^a-z0-9]+/g, " ")} `;
    let achado: { id: string; tamanho: number } | null = null;
    for (const f of fabricantes) {
      const nome = normalizarBusca(f.nome).replace(/[^a-z0-9]+/g, " ").trim();
      if (nome.length >= 2 && texto.includes(` ${nome} `) && (!achado || nome.length > achado.tamanho)) achado = { id: f.id, tamanho: nome.length };
    }
    const id = achado?.id ?? null;
    if (id === (fabricanteId ?? null)) return;
    automatica.current = id;
    setValue("fabricante_id", id, { shouldDirty: true });
  }, [descricao, fabricantes.length, escolhidaPelaPessoa]);

  if (!fabricanteId || escolhidaPelaPessoa) return null;
  return (
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
      Marca encontrada na descrição: confira.
    </Typography>
  );
}

function SecaoIdentificacao({ novo }: { novo: boolean }) {
  const { setValue, getValues } = useFormContext<ValoresProduto>();
  const descricaoInicial = useRef<string>(getValues("descricao") ?? "");
  const codigo = useWatch<ValoresProduto, "codigo">({ name: "codigo" });
  const { data: grupos = [] } = useGetList<{ id: string; nome: string }>("grupo_produto", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "nome", order: "ASC" },
  });
  const grupoInformatica = grupos.find((g) => normalizarBusca(g.nome) === "informatica")?.id ?? null;

  const sugerirGrupo = useCallback(
    (ncm: string) => {
      if (getValues("grupo_id")) return;
      void supabase.rpc("grupo_sugerido_ncm", { ncm }).then(({ data }) => {
        if (data && !getValues("grupo_id")) setValue("grupo_id", data as string, { shouldDirty: true });
      });
    },
    [getValues, setValue],
  );

  const aoEscolherNcm = useCallback(
    (ncm: string | null, descricao: string | null) => {
      if (!ncm) return;
      if (!getValues("descricao")?.trim() && descricao) {
        setValue("descricao", descricao.replace(/^[-\s]+/, ""), { shouldDirty: true });
      }
      sugerirGrupo(ncm);
    },
    [getValues, setValue, sugerirGrupo],
  );

  const usarNcmSugerido = useCallback(
    (ncm: string) => {
      setValue("ncm", ncm, { shouldDirty: true });
      sugerirGrupo(ncm);
    },
    [setValue, sugerirGrupo],
  );

  return (
    <Grid container columnSpacing={2.5} rowSpacing={1}>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <CampoArquivo campo="imagem_caminho" tipo="imagem" />
      </Grid>
      <Grid size={{ xs: 12, sm: 8, md: 9 }}>
        {/* Ordem do preenchimento: descrição (que sugere marca, NCM e imagem) → marca, modelo e unidade → NCM e grupo → fiscal. */}
        <Grid container columnSpacing={2} rowSpacing={0.5}>
          {!novo && (
            <>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextFieldMui label="Código" value={codigo ?? ""} disabled fullWidth />
              </Grid>
              <Grid size={{ xs: 6, sm: 8 }}>
                <BooleanInput source="ativo" label="Produto ativo" helperText={false} />
              </Grid>
            </>
          )}
          <Grid size={12}>
            <TextInput
              source="descricao"
              validate={required("Informe a descrição.")}
              fullWidth
              multiline
              helperText="Como sai na proposta."
              onBlur={(e: { target: { value: string } }) => {
                // Só busca imagem quando a descrição muda: a que veio do edital não é necessariamente o nosso produto (economia de crédito).
                if (e.target.value.trim() !== descricaoInicial.current.trim()) setValue("_termo_imagem", e.target.value);
              }}
            />
            <DescricaoAssistida aoEscolherNcm={usarNcmSugerido} />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <EntradaFabricante />
            {novo && <MarcaPelaDescricao />}
          </Grid>
          <Grid size={{ xs: 7, md: 4 }}>
            <TextInput source="modelo" parse={vazioParaNulo} fullWidth />
          </Grid>
          <Grid size={{ xs: 5, md: 3 }}>
            <UnidadeBase />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <EntradaNcm aoEscolher={aoEscolherNcm} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <EntradaListaCrescente source="grupo_id" reference="grupo_produto" rotuloNovo="Grupo" label="Grupo" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <InformaticaFiscal grupoInformaticaId={grupoInformatica} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <MaterialEscolar />
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <TextInput source="url" label="Página do produto (URL)" parse={vazioParaNulo} validate={validarUrl} fullWidth />
        <TextInput source="observacao" parse={vazioParaNulo} multiline fullWidth />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <CampoArquivo campo="ficha_tecnica_caminho" tipo="ficha" compacto />
      </Grid>
    </Grid>
  );
}

/* ----------------------------- Seção: embalagens ----------------------------- */

/** Fator de cada linha (em unidades-base), calculado como o banco fará. */
function fatores(linhas: LinhaEmbalagem[]): Map<string, number | null> {
  const mapa = new Map<string, number | null>();
  const porChave = new Map(linhas.map((l) => [l.chave, l]));
  const calcular = (l: LinhaEmbalagem, visitadas: Set<string>): number | null => {
    if (mapa.has(l.chave)) return mapa.get(l.chave) ?? null;
    if (l.base) return 1;
    const q = Number(String(l.quantidade).replace(",", "."));
    if (!Number.isFinite(q) || q <= 0) return null;
    if (!l.contida) return q;
    if (visitadas.has(l.chave)) return null; // ciclo
    const ref = porChave.get(l.contida);
    if (!ref) return null;
    const f = calcular(ref, new Set([...visitadas, l.chave]));
    return f === null ? null : q * f;
  };
  for (const l of linhas) mapa.set(l.chave, calcular(l, new Set()));
  return mapa;
}

function AvisoCodigoBarras({ codigo, produtoId }: { codigo: string; produtoId: string | undefined }) {
  const existente = useCodigoBarrasExistente(codigo, produtoId);
  if (existente) {
    return (
      <Typography component="span" variant="caption" color="error">
        Já cadastrado no produto{" "}
        <Link component={RouterLink} to={`/produto/${existente.produto_id}`}>
          {existente.codigo} — {existente.descricao}
        </Link>
      </Typography>
    );
  }
  if (codigo && !gtinValido(codigo)) {
    return (
      <Typography component="span" variant="caption" color="warning.main">
        Não é um GTIN/EAN válido (dígito verificador). Confira se for código de fábrica.
      </Typography>
    );
  }
  return null;
}

/** Embalagens do produto. `compacta` (cadastro rápido): sem a linha da unidade-base, já criada, e sem o texto de ajuda. */
function SecaoEmbalagens({ compacta = false }: { compacta?: boolean } = {}) {
  const { control, setValue } = useFormContext<ValoresProduto>();
  const { fields, append, remove } = useFieldArray({ control, name: "embalagens", keyName: "_rhf" });
  const linhas = useWatch<ValoresProduto, "embalagens">({ name: "embalagens" }) ?? [];
  const unidadeBase = useWatch<ValoresProduto, "unidade_base">({ name: "unidade_base" });
  const produtoId = useWatch<ValoresProduto, "id">({ name: "id" });
  const unidades = useUnidades();
  const mapaFatores = useMemo(() => fatores(linhas), [linhas]);

  // A linha-base acompanha a unidade-base escolhida nos dados do produto.
  useEffect(() => {
    const i = linhas.findIndex((l) => l.base);
    if (i >= 0 && unidadeBase && linhas[i]?.unidade !== unidadeBase) {
      setValue(`embalagens.${i}.unidade`, unidadeBase);
    }
  }, [unidadeBase, linhas, setValue]);

  const alterar = (i: number, campo: keyof LinhaEmbalagem, valor: string | boolean) =>
    setValue(`embalagens.${i}.${campo}` as const, valor as never, { shouldDirty: true });

  return (
    <Stack spacing={1.5}>
      {fields.map((f, i) => {
        const l = linhas[i];
        if (!l || (compacta && l.base)) return null;
        const fator = mapaFatores.get(l.chave);
        const referencias = linhas.filter((o) => o.chave !== l.chave && !o.base && o.ativo);
        return (
          <Card key={f._rhf} variant="outlined" sx={{ opacity: l.ativo ? 1 : 0.6 }}>
            <CardContent sx={{ pb: "12px !important" }}>
              <Grid container columnSpacing={1.5} rowSpacing={1} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 3 }}>
                  {l.base ? (
                    <TextFieldMui label="Unidade-base" value={unidadeBase || "—"} disabled fullWidth helperText="Fator 1" />
                  ) : (
                    <SeletorUnidade
                      valor={l.unidade || null}
                      aoMudar={(s) => alterar(i, "unidade", s ?? "")}
                      unidades={unidades}
                      {...(!l.unidade ? { erro: "Obrigatória" } : {})}
                    />
                  )}
                </Grid>
                {!l.base && (
                  <>
                    <Grid size={{ xs: 5, sm: 2 }}>
                      <TextFieldMui
                        label="Quantidade"
                        value={l.quantidade}
                        onChange={(e) => alterar(i, "quantidade", e.target.value)}
                        inputMode="decimal"
                        required
                        fullWidth
                        error={fator === null}
                      />
                    </Grid>
                    <Grid size={{ xs: 7, sm: 3 }}>
                      <TextFieldMui
                        select
                        label="de"
                        value={l.contida}
                        onChange={(e) => alterar(i, "contida", e.target.value)}
                        fullWidth
                        helperText={fator ? `= ${formatarNumero(fator)} ${unidadeBase || ""}` : "Fator inválido"}
                        error={fator === null}
                      >
                        <MenuItem value="">{unidadeBase || "Unidade-base"} (unidade-base)</MenuItem>
                        {referencias.map((r) => (
                          <MenuItem key={r.chave} value={r.chave}>
                            {r.unidade || "(sem unidade)"}
                            {mapaFatores.get(r.chave) ? ` com ${formatarNumero(mapaFatores.get(r.chave))}` : ""}
                          </MenuItem>
                        ))}
                      </TextFieldMui>
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 12, sm: l.base ? 5 : 4 }}>
                  <CampoCodigoBarras
                    valor={l.codigo_barras}
                    aoMudar={(c) => alterar(i, "codigo_barras", c)}
                    aviso={<AvisoCodigoBarras codigo={l.codigo_barras} produtoId={produtoId} />}
                  />
                </Grid>
                {l.base && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextFieldMui
                      label="Descrição da embalagem"
                      value={l.descricao}
                      onChange={(e) => alterar(i, "descricao", e.target.value)}
                      fullWidth
                    />
                  </Grid>
                )}
                {!l.base && (
                  <Grid size={{ xs: 12, sm: 10 }}>
                    <TextFieldMui
                      label="Descrição da embalagem (opcional)"
                      value={l.descricao}
                      onChange={(e) => alterar(i, "descricao", e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                )}
                {!l.base && (
                  <Grid size={{ xs: 12, sm: 2 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    {l.id ? (
                      <FormControlLabel
                        control={<Switch size="small" checked={l.ativo} onChange={(e) => alterar(i, "ativo", e.target.checked)} />}
                        label="Ativa"
                      />
                    ) : (
                      <Tooltip title="Remover linha">
                        <IconButton onClick={() => remove(i)} aria-label="Remover embalagem">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Grid>
                )}
              </Grid>
              {l.codigo_legado && (
                <Typography variant="caption" color="text.secondary">
                  Código legado (BASE DE DADOS): {l.codigo_legado}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            append({
              chave: novaChave(),
              base: false,
              unidade: "",
              quantidade: "",
              contida: "",
              descricao: "",
              codigo_barras: "",
              ativo: true,
            })
          }
        >
          Adicionar embalagem
        </Button>
      </Box>
      {!compacta && (
        <Typography variant="caption" color="text.secondary">
          Informe a quantidade em unidades-base ou em outra embalagem (ex.: fardo com 10 caixas). As cotações já registradas
          guardam o fator da data em que foram feitas.
        </Typography>
      )}
    </Stack>
  );
}

/* ------------------------- Seção: imagem e ficha técnica ------------------------- */
/** Busca grátis: abre o Google Imagens com a descrição; copie a imagem e cole no campo (Ctrl+V). */
function LinkGoogleImagens({ termo }: { termo: string }) {
  return (
    <Button size="small" href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(termo.trim().slice(0, 150))}`} target="_blank" rel="noopener noreferrer">
      Google Imagens (grátis)
    </Button>
  );
}

interface ImagemAchada {
  miniatura: string;
  url: string;
  titulo: string;
  pagina: string | null;
  fonte: string | null;
}

interface RespostaImagens {
  ok?: boolean;
  erro?: string;
  bloqueado?: boolean;
  motivo?: string;
  imagens?: ImagemAchada[];
  usadas?: number;
  limite?: number;
  reinicia_em?: string;
  imagem_caminho?: string;
}

/** Chama a Edge Function "imagens" e devolve o corpo, com a mensagem do servidor quando houver erro. */
async function chamarImagens(corpo: Record<string, unknown>): Promise<RespostaImagens> {
  const { data, error } = await supabase.functions.invoke<RespostaImagens>("imagens", { body: corpo });
  if (error || !data?.ok) {
    let mensagem = error?.message ?? data?.erro ?? "falha";
    const contexto = (error as { context?: Response } | null)?.context;
    if (contexto) mensagem = ((await contexto.json().catch(() => null)) as { erro?: string } | null)?.erro ?? mensagem;
    throw new Error(mensagem);
  }
  return data;
}

const dataCurta = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

/**
 * Sugestão de imagem no próprio campo (D114): com a descrição confirmada (ao sair do campo, ou já preenchida no cadastro
 * rápido), busca uma vez e mostra duas opções para escolher com um clique. "Ver mais" abre as demais da mesma busca
 * (a busca fica guardada no servidor e não gasta outro crédito).
 */
function SugestaoImagem({ aoEscolher, aoVerMais }: { aoEscolher: (caminho: string) => void; aoVerMais: () => void }) {
  const termo = (useWatch<ValoresProduto, "_termo_imagem">({ name: "_termo_imagem" }) ?? "").trim();
  const descricao = (useWatch<ValoresProduto, "descricao">({ name: "descricao" }) ?? "").trim();
  const produtoId = useWatch<ValoresProduto, "id">({ name: "id" });
  const [resposta, setResposta] = useState<RespostaImagens | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);

  useEffect(() => {
    setResposta(null);
    setErro(null);
    if (termo.length < 10) return;
    let ativo = true;
    chamarImagens({ acao: "buscar", termo })
      .then((r) => ativo && setResposta(r))
      .catch((e: Error) => ativo && setErro(e.message));
    return () => {
      ativo = false;
    };
  }, [termo]);

  async function escolher(img: ImagemAchada) {
    setBaixando(img.url);
    setErro(null);
    try {
      const r = await chamarImagens({ acao: "baixar", url: img.url, produto_id: produtoId ?? null });
      if (r.imagem_caminho) aoEscolher(r.imagem_caminho);
    } catch (e) {
      setErro(`${(e as Error).message} Escolha outra.`);
    } finally {
      setBaixando(null);
    }
  }

  if (termo.length < 10) {
    // Sem descrição confirmada (produto já gravado, por exemplo): só o botão, sem gastar busca sozinho.
    return descricao.length >= 3 ? (
      <Button size="small" onClick={aoVerMais}>
        Buscar imagem
      </Button>
    ) : null;
  }
  const opcoes = (resposta?.imagens ?? []).slice(0, 2);
  return (
    <Box>
      {!resposta && !erro && (
        <Typography variant="caption" color="text.secondary">
          Procurando imagem…
        </Typography>
      )}
      {opcoes.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">
            Sugestões — clique para usar:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {opcoes.map((img) => (
              <Box
                key={img.url}
                onClick={() => !baixando && void escolher(img)}
                title={img.fonte ?? img.titulo}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 0.5,
                  cursor: baixando ? "wait" : "pointer",
                  opacity: baixando && baixando !== img.url ? 0.5 : 1,
                  "&:hover": { borderColor: "secondary.main" },
                }}
              >
                <Box component="img" src={img.miniatura} alt={img.titulo} sx={{ width: "100%", height: 64, objectFit: "contain", display: "block" }} />
                <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10 }}>
                  {baixando === img.url ? "Baixando…" : (img.fonte ?? "")}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
      {resposta?.bloqueado && (
        <Typography variant="caption" color="text.secondary" display="block">
          Busca automática pausada{resposta.reinicia_em ? ` até ${dataCurta(resposta.reinicia_em)}` : ""}.
        </Typography>
      )}
      {erro && (
        <Typography variant="caption" color="warning.main" display="block">
          {erro}
        </Typography>
      )}
      <Button size="small" onClick={aoVerMais} sx={{ mt: 0.5 }}>
        {opcoes.length > 0 ? "Ver mais / buscar" : "Buscar imagem"}
      </Button>
    </Box>
  );
}

/**
 * Busca automática de imagens (Brave, D113). A trava de consumo fica no servidor: no limite do crédito grátis,
 * a busca não é feita e a janela oferece o Google Imagens até a renovação.
 */
function DialogoBuscaImagem({
  termo,
  produtoId,
  aoFechar,
  aoEscolher,
}: {
  termo: string;
  produtoId: string | null;
  aoFechar: () => void;
  aoEscolher: (caminho: string) => void;
}) {
  const [resposta, setResposta] = useState<RespostaImagens | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState<string | null>(null);
  // Termo da busca: começa pela descrição (mesma busca da sugestão, sem novo crédito); pode ser refinado.
  const [texto, setTexto] = useState(termo.trim());
  const [buscado, setBuscado] = useState(termo.trim());

  useEffect(() => {
    let ativo = true;
    setResposta(null);
    setErro(null);
    chamarImagens({ acao: "buscar", termo: buscado })
      .then((r) => ativo && setResposta(r))
      .catch((e: Error) => ativo && setErro(e.message));
    return () => {
      ativo = false;
    };
  }, [buscado]);

  async function escolher(img: ImagemAchada) {
    setBaixando(img.url);
    setErro(null);
    try {
      const r = await chamarImagens({ acao: "baixar", url: img.url, produto_id: produtoId });
      if (r.imagem_caminho) aoEscolher(r.imagem_caminho);
    } catch (e) {
      setErro(`${(e as Error).message} Escolha outra imagem.`);
    } finally {
      setBaixando(null);
    }
  }

  const imagens = resposta?.imagens ?? [];
  const consumo =
    resposta?.limite !== undefined && resposta.usadas !== undefined
      ? `${resposta.usadas} de ${resposta.limite} buscas do crédito grátis neste ciclo${resposta.reinicia_em ? ` · renova em ${dataCurta(resposta.reinicia_em)}` : ""}.`
      : null;
  return (
    <Dialog open onClose={baixando ? undefined : aoFechar} fullWidth maxWidth="md">
      <DialogTitle>Imagem do produto</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} sx={{ pt: 1, mb: 1.5 }}>
          <TextFieldMui
            size="small"
            label="Buscar por"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && texto.trim().length >= 3) setBuscado(texto.trim());
            }}
            fullWidth
            helperText="Refine se preciso (ex.: marca e modelo). Cada termo novo usa uma busca do crédito."
          />
          <Button variant="outlined" onClick={() => setBuscado(texto.trim())} disabled={texto.trim().length < 3 || texto.trim() === buscado} sx={{ height: 40, flexShrink: 0 }}>
            Buscar
          </Button>
        </Stack>
        {!resposta && !erro && <Typography variant="body2">Buscando…</Typography>}
        {erro && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {erro}
          </Alert>
        )}
        {resposta?.bloqueado && (
          <Alert severity="info" sx={{ mb: 1 }} action={<LinkGoogleImagens termo={texto} />}>
            {resposta.motivo ?? "Busca automática indisponível."}
            {resposta.reinicia_em ? ` Volta em ${dataCurta(resposta.reinicia_em)}.` : ""} Enquanto isso, use o Google Imagens (grátis) e cole a imagem no
            campo (Ctrl+V).
          </Alert>
        )}
        {resposta && !resposta.bloqueado && imagens.length === 0 && <Typography variant="body2">Nenhuma imagem encontrada para esta descrição.</Typography>}
        <Grid container spacing={1.5}>
          {imagens.map((img) => (
            <Grid key={img.url} size={{ xs: 6, sm: 4, md: 3 }}>
              <Box
                onClick={() => !baixando && void escolher(img)}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 0.75,
                  cursor: baixando ? "wait" : "pointer",
                  opacity: baixando && baixando !== img.url ? 0.5 : 1,
                  "&:hover": { borderColor: "secondary.main" },
                }}
              >
                <Box component="img" src={img.miniatura} alt={img.titulo} loading="lazy" sx={{ width: "100%", height: 120, objectFit: "contain" }} />
                <Typography variant="caption" color="text.secondary" display="block" noWrap title={img.titulo}>
                  {baixando === img.url ? "Baixando…" : (img.fonte ?? img.titulo)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        {consumo && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            {consumo} Prefira a imagem do site do fabricante.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <LinkGoogleImagens termo={texto} />
        <Button onClick={aoFechar} disabled={baixando !== null}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function CampoArquivo({ campo, tipo, compacto = false }: { campo: "imagem_caminho" | "ficha_tecnica_caminho"; tipo: "imagem" | "ficha"; compacto?: boolean }) {
  const { field } = useInput({ source: campo });
  const produtoId = useWatch<ValoresProduto, "id">({ name: "id" });
  const notify = useNotify();
  const [enviando, setEnviando] = useState(false);
  const caminho = (field.value as string | null) ?? null;
  const url = urlPublica(caminho);
  const aceitos = tipo === "imagem" ? ["image/png", "image/jpeg", "image/webp"] : ["application/pdf"];
  const descricao = useWatch<ValoresProduto, "descricao">({ name: "descricao" }) ?? "";
  const [buscando, setBuscando] = useState(false);

  async function enviar(arquivo: File) {
    if (arquivo.size > 10 * 1024 * 1024) {
      notify("O arquivo precisa ter no máximo 10 MB.", { type: "error" });
      return;
    }
    setEnviando(true);
    const extensao = { "image/png": "png", "image/jpeg": "jpg", "image/webp": "webp", "application/pdf": "pdf" }[arquivo.type] ?? "bin";
    const pasta = `${tipo === "imagem" ? "imagens" : "fichas"}/${produtoId ?? "novos"}`;
    const destino = `${pasta}/${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 6)}.${extensao}`;
    const { error } = await supabase.storage.from(BUCKET).upload(destino, arquivo, { contentType: arquivo.type, upsert: false });
    setEnviando(false);
    if (error) {
      notify(`Falha ao enviar o arquivo: ${error.message}`, { type: "error" });
      return;
    }
    field.onChange(destino);
    notify(tipo === "imagem" ? "Imagem anexada. Salve o produto para gravar." : "Ficha técnica anexada. Salve o produto para gravar.", {
      type: "info",
    });
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {tipo === "imagem" ? "Imagem do produto" : "Ficha técnica (PDF)"}
      </Typography>
      <ZonaArquivo
        alturaMinima={compacto ? 96 : 200}
        aceitos={aceitos}
        rotulo={tipo === "imagem" ? "Enviar imagem do produto" : "Enviar ficha técnica"}
        enviando={enviando}
        aoReceber={(a) => void enviar(a)}
      >
        {tipo === "imagem" && url && <Box component="img" src={url} alt="Imagem do produto" sx={{ maxHeight: 150, maxWidth: "100%" }} />}
        {tipo === "ficha" && url && (
          <Button href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
            Abrir ficha técnica
          </Button>
        )}
      </ZonaArquivo>
      {tipo === "imagem" && !caminho && <SugestaoImagem aoEscolher={(c) => field.onChange(c)} aoVerMais={() => setBuscando(true)} />}
      {tipo === "imagem" && caminho && descricao.trim().length >= 3 && (
        <Box>
          <Button size="small" variant="outlined" onClick={() => setBuscando(true)}>
            Trocar imagem
          </Button>
        </Box>
      )}
      {buscando && (
        <DialogoBuscaImagem
          termo={descricao}
          produtoId={produtoId ?? null}
          aoFechar={() => setBuscando(false)}
          aoEscolher={(c) => {
            field.onChange(c);
            setBuscando(false);
            notify("Imagem anexada. Salve o produto para gravar.", { type: "info" });
          }}
        />
      )}
      {caminho && (
        <Box>
          <Button size="small" color="inherit" onClick={() => field.onChange(null)}>
            Remover {tipo === "imagem" ? "imagem" : "ficha técnica"}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

interface Sugestao {
  id: string;
  url: string;
  titulo: string | null;
  fonte: string | null;
  termo_busca: string | null;
}

/** Sugestões de imagem pendentes (migração em lote): "Selecionar esta imagem" ou "Nenhuma corresponde". */
function SugestoesImagem({ produtoId }: { produtoId: string }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [aprovando, setAprovando] = useState<string | null>(null);
  const { data: sugestoes = [] } = useGetList<Sugestao>("produto_imagem_sugestao", {
    filter: { produto_id: produtoId, situacao: "pendente" },
    pagination: { page: 1, perPage: 12 },
    sort: { field: "criado_em", order: "ASC" },
  });
  if (sugestoes.length === 0) return null;

  async function aprovar(s: Sugestao) {
    setAprovando(s.id);
    const { error } = await supabase.functions.invoke("aprovar-imagem", { body: { sugestao_id: s.id } });
    setAprovando(null);
    if (error) {
      let mensagem = error.message;
      const contexto = (error as { context?: Response }).context;
      if (contexto && typeof contexto.json === "function") {
        try {
          const corpo = (await contexto.json()) as { erro?: string };
          if (corpo.erro) mensagem = corpo.erro;
        } catch {
          // resposta sem JSON: mantém a mensagem genérica
        }
      }
      notify(`Não foi possível vincular a imagem: ${mensagem}`, { type: "error" });
      return;
    }
    notify("Imagem vinculada ao produto.", { type: "success" });
    refresh();
  }

  async function recusarTodas() {
    const agora = new Date().toISOString();
    const resultados = await Promise.all(
      sugestoes.map((s) => supabase.from("produto_imagem_sugestao").update({ situacao: "recusada", decidida_em: agora }).eq("id", s.id)),
    );
    const falha = resultados.find((r) => r.error);
    if (falha?.error) notify(`Falha ao registrar a decisão: ${falha.error.message}`, { type: "error" });
    else notify("Sugestões recusadas. Envie uma imagem própria, se necessário.", { type: "info" });
    refresh();
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          Imagens sugeridas — selecione a que corresponde a este produto
        </Typography>
        <Grid container spacing={2}>
          {sugestoes.map((s) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={s.id}>
              <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
                  <Box component="img" src={s.url} alt={s.titulo ?? "Sugestão"} sx={{ maxHeight: 110, maxWidth: "100%" }} />
                </Box>
                <CardContent sx={{ py: 0.5, flex: 1 }}>
                  <Typography variant="caption" display="block">
                    {s.titulo ?? "Sem título"}
                  </Typography>
                  {s.fonte && (
                    <Typography variant="caption" color="text.secondary">
                      {s.fonte}
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" variant="contained" fullWidth disabled={aprovando !== null} onClick={() => void aprovar(s)}>
                    {aprovando === s.id ? "Vinculando…" : "Selecionar esta imagem"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button onClick={() => void recusarTodas()} disabled={aprovando !== null}>
            Nenhuma sugestão corresponde ao produto
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/* -------------------------------- Formulário -------------------------------- */

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1.5 }}>
        {titulo}
      </Typography>
      {children}
    </Box>
  );
}

function payload(v: ValoresProduto) {
  return {
    ...(v.id ? { id: v.id } : {}),
    descricao: v.descricao,
    fabricante_id: v.fabricante_id,
    modelo: v.modelo,
    grupo_id: v.grupo_id,
    informatica: Boolean(v.informatica),
    material_escolar: Boolean(v.material_escolar),
    ncm: v.ncm,
    unidade_base: v.unidade_base,
    url: v.url,
    observacao: v.observacao,
    ativo: v.ativo,
    imagem_caminho: v.imagem_caminho,
    ficha_tecnica_caminho: v.ficha_tecnica_caminho,
    embalagens: v.embalagens.map((l) =>
      l.base
        ? { base: true, ...(l.id ? { id: l.id } : {}), descricao: l.descricao, codigo_barras: l.codigo_barras }
        : {
            ...(l.id ? { id: l.id } : {}),
            chave: l.chave,
            unidade: l.unidade,
            descricao: l.descricao,
            quantidade: Number(String(l.quantidade).replace(",", ".")),
            contida: l.contida || null,
            codigo_barras: l.codigo_barras,
            ativo: l.ativo,
          },
    ),
  };
}

function validar(v: ValoresProduto): string | null {
  const mapa = fatores(v.embalagens);
  for (const l of v.embalagens) {
    if (l.base) continue;
    if (!l.unidade) return "Todas as embalagens precisam de unidade.";
    if (!mapa.get(l.chave)) return `Embalagem ${l.unidade}: quantidade ou referência inválida.`;
  }
  const codigos = v.embalagens.map((l) => l.codigo_barras.trim()).filter(Boolean);
  if (new Set(codigos).size !== codigos.length) return "O mesmo código de barras aparece em duas embalagens.";
  return null;
}

interface OrigemDuplicacao {
  id: string;
  codigo: string;
  descricao: string;
  copiarCotacoes: boolean;
}

function FormularioProduto({ inicial, duplicadoDe }: { inicial: ValoresProduto; duplicadoDe?: OrigemDuplicacao | null }) {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [erro, setErro] = useState<string | null>(null);
  const novo = !inicial.id;

  async function salvar(valores: Record<string, unknown>) {
    const v = valores as unknown as ValoresProduto;
    setErro(null);
    const problema = validar(v);
    if (problema) {
      setErro(problema);
      return;
    }
    const { data, error } = await supabase.rpc("salvar_produto", { dados: payload(v) });
    if (error) {
      const mensagem = /produto_embalagem_codigo_barras_unico/.test(error.message)
        ? "Código de barras já cadastrado em outro produto."
        : error.message;
      setErro(`Não foi possível salvar: ${mensagem}`);
      return;
    }
    let mensagem = novo ? "Produto cadastrado." : "Produto atualizado.";
    if (novo && duplicadoDe?.copiarCotacoes) {
      const { data: copiadas, error: erroCopia } = await supabase.rpc("copiar_cotacoes", {
        produto_origem: duplicadoDe.id,
        produto_destino: data as string,
      });
      mensagem = erroCopia
        ? `Produto cadastrado, mas as cotações não foram copiadas: ${erroCopia.message}`
        : `Produto cadastrado com ${String(copiadas)} cotação(ões) copiada(s).`;
    }
    notify(mensagem, { type: "success" });
    if (novo) redirect("edit", "produto", data as string);
    else refresh();
  }

  return (
    <Form record={inicial as unknown as Record<string, unknown>} onSubmit={salvar} warnWhenUnsavedChanges>
      <Card>
        <CardContent>
          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}
          {duplicadoDe && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cópia do produto {duplicadoDe.codigo} — {duplicadoDe.descricao}. Ajuste o que for diferente e cadastre.
              {duplicadoDe.copiarCotacoes ? " As cotações serão copiadas após o cadastro." : ""} Os códigos de barras não são
              copiados.
            </Alert>
          )}
          <Secao titulo="Identificação">
            <SecaoIdentificacao novo={novo} />
          </Secao>
          <Divider sx={{ mb: 2 }} />
          <Secao titulo="Embalagens">
            <SecaoEmbalagens />
          </Secao>
        </CardContent>
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
            px: 2,
            py: 1.5,
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <SaveButton label={novo ? "Cadastrar produto" : "Salvar alterações"} alwaysEnable={novo} />
          {novo && (
            <Typography variant="caption" color="text.secondary">
              Depois de cadastrar, as cotações ficam na aba "Cotações e análise".
            </Typography>
          )}
        </Box>
      </Card>
    </Form>
  );
}

function valoresIniciais(p: Produto | null, embalagens: EmbalagemGravada[]): ValoresProduto {
  if (!p) {
    return {
      descricao: "",
      fabricante_id: null,
      modelo: null,
      grupo_id: null,
      informatica: false,
      material_escolar: false,
      ncm: null,
      unidade_base: "UN",
      url: null,
      observacao: null,
      ativo: true,
      imagem_caminho: null,
      ficha_tecnica_caminho: null,
      embalagens: [{ chave: "base", base: true, unidade: "UN", quantidade: "1", contida: "", descricao: "", codigo_barras: "", ativo: true }],
    };
  }
  const ordenadas = [...embalagens].sort((a, b) => Number(b.base) - Number(a.base) || Number(a.fator_base) - Number(b.fator_base));
  return {
    id: p.id,
    codigo: p.codigo,
    descricao: p.descricao,
    fabricante_id: p.fabricante_id,
    modelo: p.modelo,
    grupo_id: p.grupo_id,
    informatica: p.informatica,
    material_escolar: Boolean(p.material_escolar),
    ncm: p.ncm,
    unidade_base: p.unidade_base,
    url: p.url,
    observacao: p.observacao,
    ativo: p.ativo,
    imagem_caminho: p.imagem_caminho,
    ficha_tecnica_caminho: p.ficha_tecnica_caminho,
    embalagens: ordenadas.map((e) => ({
      chave: e.id,
      id: e.id,
      base: e.base,
      unidade: e.unidade,
      quantidade: String(Number(e.quantidade)),
      contida: e.contida_id ?? "",
      descricao: e.descricao ?? "",
      codigo_barras: e.codigo_barras ?? "",
      ativo: e.ativo,
      codigo_legado: e.codigo_legado,
    })),
  };
}

/** Valores para um produto novo a partir de outro (duplicar): sem código, sem ids e sem códigos de barras. */
function valoresDuplicados(p: Produto, embalagens: EmbalagemGravada[]): ValoresProduto {
  const { id: _id, codigo: _codigo, ...original } = valoresIniciais(p, embalagens);
  const chave = (id: string) => `dup-${id}`;
  return {
    ...original,
    ativo: true,
    embalagens: original.embalagens
      .filter((l) => l.ativo)
      .map((l) => ({
        chave: l.base ? "base" : chave(l.chave),
        base: l.base,
        unidade: l.unidade,
        quantidade: l.quantidade,
        contida: l.contida ? chave(l.contida) : "",
        descricao: l.descricao,
        codigo_barras: "",
        ativo: true,
      })),
  };
}

interface EstadoDuplicacao {
  duplicarDe?: string;
  copiarCotacoes?: boolean;
}

function CriarDuplicado({ origemId, copiarCotacoes }: { origemId: string; copiarCotacoes: boolean }) {
  const { data: origem } = useGetOne<Produto>("produto", { id: origemId });
  const { data: embalagens } = useGetList<EmbalagemGravada>("produto_embalagem", {
    filter: { produto_id: origemId },
    pagination: { page: 1, perPage: 200 },
    sort: { field: "fator_base", order: "ASC" },
  });
  if (!origem || !embalagens) return <Loading />;
  return (
    <FormularioProduto
      inicial={valoresDuplicados(origem, embalagens)}
      duplicadoDe={{ id: origem.id, codigo: origem.codigo, descricao: origem.descricao, copiarCotacoes }}
    />
  );
}

export function ProdutoCriar() {
  const estado = (useLocation().state ?? {}) as EstadoDuplicacao;
  return (
    <CreateBase resource="produto">
      <Box sx={{ mt: 2 }}>
        <Title title={estado.duplicarDe ? "Novo produto (cópia)" : "Novo produto"} />
        {estado.duplicarDe ? (
          <CriarDuplicado origemId={estado.duplicarDe} copiarCotacoes={Boolean(estado.copiarCotacoes)} />
        ) : (
          <FormularioProduto inicial={valoresIniciais(null, [])} />
        )}
      </Box>
    </CreateBase>
  );
}

/** Pergunta se as cotações também devem ser copiadas e abre o cadastro da cópia. */
function BotaoDuplicar({ produtoId }: { produtoId: string }) {
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const ir = (copiarCotacoes: boolean) =>
    navigate("/produto/create", { state: { duplicarDe: produtoId, copiarCotacoes } satisfies EstadoDuplicacao });
  return (
    <>
      <Button startIcon={<CopyIcon />} onClick={() => setAberto(true)}>
        Duplicar
      </Button>
      <Dialog open={aberto} onClose={() => setAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Duplicar produto</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O cadastro será aberto como um produto novo, com os mesmos dados e embalagens, para você ajustar o que for
            diferente. Deseja copiar também as cotações deste produto?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button onClick={() => ir(false)}>Sem cotações</Button>
          <Button variant="contained" onClick={() => ir(true)}>
            Com cotações
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function ConteudoEdicao() {
  const { record, isPending } = useEditContext<Produto>();
  const [aba, setAba] = useState(0);
  const { data: embalagens, isPending: carregandoEmbalagens } = useGetList<EmbalagemGravada>(
    "produto_embalagem",
    { filter: { produto_id: record?.id }, pagination: { page: 1, perPage: 200 }, sort: { field: "fator_base", order: "ASC" } },
    { enabled: Boolean(record?.id) },
  );
  if (isPending || carregandoEmbalagens || !record || !embalagens) return <Loading />;
  // A chave força o formulário a reiniciar quando os dados gravados mudam (após salvar).
  const chave = `${record.id}-${String((record as unknown as { atualizado_em?: string }).atualizado_em)}-${embalagens.map((e) => `${e.id}:${e.fator_base}:${e.codigo_barras}:${e.ativo}`).join("|")}`;
  return (
    <Box sx={{ mt: 2 }}>
      <Title title={`Produto ${record.codigo} — ${record.descricao}`} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Tabs value={aba} onChange={(_, v: number) => setAba(v)}>
          <Tab label="Cadastro" />
          <Tab label="Cotações e análise" />
        </Tabs>
        <BotaoDuplicar produtoId={record.id} />
      </Stack>
      {aba === 0 && (
        <>
          <FormularioProduto key={chave} inicial={valoresIniciais(record, embalagens)} />
          <Box sx={{ mt: 2 }}>
            <SugestoesImagem produtoId={record.id} />
          </Box>
        </>
      )}
      {aba === 1 && (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {record.codigo} — {record.descricao}
            </Typography>
            <PainelCotacoes />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export function ProdutoEditar() {
  return (
    <EditBase resource="produto" mutationMode="pessimistic">
      <ConteudoEdicao />
    </EditBase>
  );
}

/* ----------------------------- Cadastro rápido (precificação) ----------------------------- */

/** A unidade-base já existe: o botão já cria a próxima embalagem (caixa, fardo) e abre a seção. */
function BotaoPrimeiraEmbalagem({ aoAdicionar }: { aoAdicionar: () => void }) {
  const { getValues, setValue } = useFormContext<ValoresProduto>();
  return (
    <Button
      size="small"
      startIcon={<AddIcon />}
      sx={{ mt: 1 }}
      onClick={() => {
        setValue(
          "embalagens",
          [
            ...(getValues("embalagens") ?? []),
            { chave: novaChave(), base: false, unidade: "", quantidade: "", contida: "", descricao: "", codigo_barras: "", ativo: true },
          ],
          { shouldDirty: true },
        );
        aoAdicionar();
      }}
    >
      Adicionar embalagem
    </Button>
  );
}

/** Unidade-base sugerida pela unidade do item da licitação (só no cadastro rápido). */
function UnidadeDoItem({ unidadeItem }: { unidadeItem: string | null }) {
  const { setValue } = useFormContext<ValoresProduto>();
  const unidades = useUnidades();
  const aplicada = useRef(false);
  useEffect(() => {
    if (aplicada.current || unidades.length === 0 || !unidadeItem) return;
    aplicada.current = true;
    const alvo = normalizarBusca(unidadeItem);
    const u = unidades.find((x) => normalizarBusca(x.sigla) === alvo || normalizarBusca(x.descricao) === alvo);
    if (u) setValue("unidade_base", u.sigla, { shouldDirty: true });
  }, [unidades.length, unidadeItem, setValue]);
  if (!unidadeItem) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      Unidade do item na licitação: {unidadeItem}.
    </Typography>
  );
}

/**
 * Cadastro rápido de produto, aberto da precificação (D112): a mesma seção "Identificação" da ficha do produto
 * (marca pela descrição, NCM sugerido, grupo, informática pelo Anexo III, material escolar, imagem e ficha técnica),
 * gravada pela mesma função salvar_produto. Com produtoId, edita o produto escolhido no item (lápis ao lado
 * do produto, como o da cotação); sem ele, cadastra um novo.
 */
export function DialogoProdutoRapido({
  produtoId,
  textoInicial,
  unidadeItem,
  aoFechar,
  aoCriar,
}: {
  produtoId?: string;
  textoInicial: string;
  unidadeItem: string | null;
  aoFechar: () => void;
  aoCriar: (produtoId: string) => void;
}) {
  const editar = Boolean(produtoId);
  const { data: registro, isPending: carregandoProduto } = useGetOne<Produto>("produto", { id: produtoId ?? "" }, { enabled: editar });
  const { data: gravadas, isPending: carregandoEmbalagens } = useGetList<EmbalagemGravada>(
    "produto_embalagem",
    { filter: { produto_id: produtoId }, pagination: { page: 1, perPage: 200 }, sort: { field: "fator_base", order: "ASC" } },
    { enabled: editar },
  );
  const inicial = useMemo(
    () => (editar ? (registro && gravadas ? valoresIniciais(registro, gravadas) : null) : { ...valoresIniciais(null, []), descricao: textoInicial }),
    [editar, registro, gravadas, textoInicial],
  );
  if (editar && (carregandoProduto || carregandoEmbalagens || !inicial)) {
    return (
      <Dialog open onClose={aoFechar} fullWidth maxWidth="md">
        <DialogContent>
          <Loading />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <FormularioProdutoRapido
      editar={editar}
      inicial={inicial as ValoresProduto}
      unidadeItem={unidadeItem}
      comEmbalagens={editar && (gravadas?.length ?? 0) > 1}
      aoFechar={aoFechar}
      aoCriar={aoCriar}
    />
  );
}

function FormularioProdutoRapido({
  editar,
  inicial,
  unidadeItem,
  comEmbalagens,
  aoFechar,
  aoCriar,
}: {
  editar: boolean;
  inicial: ValoresProduto;
  unidadeItem: string | null;
  comEmbalagens: boolean;
  aoFechar: () => void;
  aoCriar: (produtoId: string) => void;
}) {
  const notify = useNotify();
  const [erro, setErro] = useState<string | null>(null);
  const [embalagens, setEmbalagens] = useState(comEmbalagens);

  async function salvar(valores: Record<string, unknown>) {
    const v = valores as unknown as ValoresProduto;
    setErro(null);
    const problema = validar(v);
    if (problema) {
      setErro(problema);
      return;
    }
    const { data, error } = await supabase.rpc("salvar_produto", { dados: payload(v) });
    if (error) {
      const mensagem = /produto_embalagem_codigo_barras_unico/.test(error.message) ? "Código de barras já cadastrado em outro produto." : error.message;
      setErro(`Não foi possível ${editar ? "gravar" : "cadastrar"}: ${mensagem}`);
      return;
    }
    notify(editar ? "Produto atualizado." : "Produto cadastrado. Registre a cotação.", { type: "success" });
    aoCriar(data as string);
  }

  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="md">
      {/* Mesmo recurso da ficha: rótulos e listas iguais aos do cadastro completo. */}
      <ResourceContextProvider value="produto">
      <Form record={inicial as unknown as Record<string, unknown>} onSubmit={salvar}>
        <DialogTitle>{editar ? `Editar produto ${inicial.codigo ?? ""}` : "Cadastrar produto"}</DialogTitle>
        <DialogContent>
          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <SecaoIdentificacao novo={!editar} />
            {!editar && <UnidadeDoItem unidadeItem={unidadeItem} />}
          </Box>
          {embalagens ? (
            <Box sx={{ mt: 1.5 }}>
              <SecaoEmbalagens compacta />
            </Box>
          ) : (
            <BotaoPrimeiraEmbalagem aoAdicionar={() => setEmbalagens(true)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={aoFechar}>Cancelar</Button>
          <SaveButton label={editar ? "Gravar produto" : "Cadastrar produto"} alwaysEnable />
        </DialogActions>
      </Form>
      </ResourceContextProvider>
    </Dialog>
  );
}
